import React, { useEffect, useRef } from 'react';
import { ScrollView, StyleSheet, View, Animated } from 'react-native';
import { useLanguage } from '../../../locale';

export const SurveyDetailSkeleton: React.FC = () => {
  const [t] = useLanguage();
  const fadeAnim = useRef(new Animated.Value(0.3)).current;

  // Create a pulsing animation for the skeleton
  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(fadeAnim, {
          toValue: 0.7,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(fadeAnim, {
          toValue: 0.3,
          duration: 800,
          useNativeDriver: true,
        }),
      ])
    ).start();
  }, [fadeAnim]);

  // Placeholder for card-based questions
  const renderSkeletonCardQuestion = (index: number) => {
    return (
      <View key={`card-question-${index}`} style={styles.questionContainer}>
        <Animated.View
          style={[
            styles.placeholderText,
            { opacity: fadeAnim, width: `${70 - index * 10}%` },
          ]}
        />

        <View style={styles.optionsContainer}>
          {[0, 1, 2, 3].map((optionIndex) => (
            <View key={optionIndex} style={styles.optionCardPlaceholder}>
              <Animated.View
                style={[styles.placeholderIcon, { opacity: fadeAnim }]}
              />
              <Animated.View
                style={[
                  styles.placeholderOptionText,
                  { opacity: fadeAnim, width: `${60 + optionIndex * 10}%` },
                ]}
              />
            </View>
          ))}
        </View>
      </View>
    );
  };

  // Placeholder for text questions
  const renderSkeletonTextQuestion = () => {
    return (
      <View key="text-question" style={styles.questionContainer}>
        <Animated.View
          style={[styles.placeholderText, { opacity: fadeAnim, width: '60%' }]}
        />

        <Animated.View
          style={[styles.textInputPlaceholder, { opacity: fadeAnim }]}
        />
      </View>
    );
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
    >
      {/* Title placeholder */}
      <Animated.View style={[styles.placeholderTitle, { opacity: fadeAnim }]} />

      {/* Description placeholder */}
      <Animated.View
        style={[styles.placeholderDescription, { opacity: fadeAnim }]}
      />

      {/* Anonymous switch placeholder */}
      <View style={styles.anonymousContainer}>
        <Animated.View
          style={[styles.placeholderText, { opacity: fadeAnim, width: '40%' }]}
        />
        <Animated.View
          style={[styles.placeholderSwitch, { opacity: fadeAnim }]}
        />
      </View>

      {/* Question placeholders */}
      <View style={styles.questionsContainer}>
        {[0, 1].map(renderSkeletonCardQuestion)}
        {renderSkeletonTextQuestion()}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  placeholderTitle: {
    height: 28,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
    width: '60%',
    marginBottom: 8,
  },
  placeholderDescription: {
    height: 16,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
    width: '90%',
    marginTop: 8,
    marginBottom: 8,
  },
  anonymousContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 38,
    paddingVertical: 30,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  placeholderText: {
    height: 16,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
  placeholderSwitch: {
    width: 40,
    height: 22,
    backgroundColor: '#E0E0E0',
    borderRadius: 12,
  },
  questionsContainer: {
    marginBottom: 38,
  },
  questionContainer: {
    marginBottom: 38,
    paddingHorizontal: 4,
  },
  optionsContainer: {
    marginTop: 20,
    gap: 8,
  },
  optionCardPlaceholder: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 8,
    backgroundColor: '#F8F8F8',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E5E7',
  },
  placeholderIcon: {
    width: 24,
    height: 24,
    backgroundColor: '#E0E0E0',
    borderRadius: 12,
    marginRight: 16,
  },
  placeholderOptionText: {
    height: 14,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
    flex: 1,
  },
  textInputPlaceholder: {
    height: 120,
    backgroundColor: '#F0F0F0',
    borderWidth: 1,
    borderColor: '#D1D1D6',
    borderRadius: 8,
    marginTop: 8,
  },
});
